import { Field, Textarea, Checkbox, makeStyles, tokens, useId, Toaster } from '@fluentui/react-components';
import * as React from 'react';
import ProjectSelelctor from '../../../components/ProjectSelector';
import { useTranslation } from 'react-i18next';
import { useItem } from '../../../hooks/item/ItemProvider';
import { LoadingButton } from '../../../components/LoadingButton';
import { useClient } from '../../../hooks/client';
import useErrorHandling, { RequestErrorCodes } from '../../../hooks/useErrorHandling';

const useStyles = makeStyles({
  container: {
    paddingInline: tokens.spacingHorizontalXXL,
    paddingBlock: tokens.spacingVerticalXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
  },
  button: {
    width: '100%',
  },
  toast: {
    gridColumn: '1/4',
    paddingBlock: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalXS,
  },
});

const EventTab = () => {
  const toasterId = useId('event-tab-toast');
  const styles = useStyles();
  const { t } = useTranslation();
  const { emailObject, setFormValue, isValid, submit, showBcc, refetchEmailData } = useItem();
  const { configuration, refetchConfiguration } = useClient();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const { formErrors, handleError, clearErrors } = useErrorHandling(toasterId);

  const clearInvalidProjects = async () => {
    const freshConfiguration = await refetchConfiguration();
    const validProjectIds = emailObject.selectedProjects.filter((x) => freshConfiguration.availableProjects.map((y) => y.id).includes(x));
    setFormValue('selectedProjects', validProjectIds);
  };
  const handleSubmit = async () => {
    setIsLoading(true);
    const success = await submit();
    if (!success.success) {
      await handleError(success.error, clearInvalidProjects);
      if (success.error?.data?.code === RequestErrorCodes.existingEmail) {
        refetchEmailData();
      }
    }
    setIsLoading(false);
  };

  return (
    <>
      <Toaster
        toasterId={toasterId}
        offset={{ vertical: 40 }}
      />
      <div className={styles.container}>
        <Field
          label={configuration.entityDefinitions.project.pluralName}
          required
          validationState={formErrors.projectIds?.length ? 'error' : 'none'}
        >
          <ProjectSelelctor
            projectPluralName={configuration.entityDefinitions.project.pluralName}
            projects={configuration.availableProjects ?? []}
            selectedProjects={emailObject.selectedProjects}
            onChange={(ids) => {
              setFormValue('selectedProjects', ids);
              clearErrors('projectIds');
            }}
          />
        </Field>
        <Field
          required
          label={configuration.entityDefinitions.content.fieldSchemaDetails.subject.name}
          validationState={formErrors.summary?.length ? 'error' : 'none'}
        >
          <Textarea
            resize="vertical"
            value={emailObject.summary}
            onChange={(_, data) => {
              setFormValue('summary', data.value);
              clearErrors('summary');
            }}
          />
        </Field>
        {showBcc && (
          <Field>
            <Checkbox
              checked={emailObject.includeBcc}
              onChange={(_, data) => setFormValue('includeBcc', !!data.checked)}
              label={t('EVENT_TAB.INCLUDE_BCC')}
            />
          </Field>
        )}
        <LoadingButton
          loadingState={isLoading ? 'loading' : 'initial'}
          onClick={() => handleSubmit()}
          size="large"
          appearance="primary"
          className={styles.button}
          disabled={!isValid}
        >
          {t('EVENT_TAB.SEND')}
        </LoadingButton>
      </div>
    </>
  );
};

export default EventTab;
